'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { client, CLIENT_DETAILS, teamMember } from '../demo-data'
import { addressTagLabel, useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, Tag } from './app-screen'
import styles from './client-record-visual.module.css'
import { useScenario } from './use-scenario'

type FieldSpec = { label: string; value: string; at: number; accent?: boolean }

// The dossier subject — one canonical client record, shared with the KvK add-flow in
// `ClientDossierVisual` so the number, address and details never contradict each other.
const SUBJECT = client('10010')
const DETAILS = CLIENT_DETAILS['10010']

/**
 * The record's fields, in the order the dossier lists them. `compact` drops the IBAN: on the
 * homepage's module card this screen has a hundred pixels less to work with, and a field whose
 * value is cut off below the card edge is worse than one that is not there.
 */
const fields = (t: VisualCopy, compact: boolean): readonly FieldSpec[] => [
  { label: t.word.name, value: SUBJECT.name, at: 1 },
  { label: t.screen.clientRecord.clientNumber, value: SUBJECT.nr, at: 1 },
  { label: t.screen.clientRecord.email, value: DETAILS.email, at: 2, accent: true },
  { label: t.screen.clientRecord.phone, value: DETAILS.phone, at: 2, accent: true },
  { label: t.screen.clientDossier.kvkNumber, value: DETAILS.kvk, at: 3 },
  { label: t.screen.clientRecord.vatNumber, value: DETAILS.btw, at: 3 },
  ...(compact ? [] : [{ label: 'IBAN', value: DETAILS.iban, at: 3 }]),
]

/** The colleague who owns this client, straight off the record. */
const MANAGER = teamMember(SUBJECT.manager)

const ADDRESSES = DETAILS.addresses
const CONTACTS = DETAILS.contacts

/**
 * The dossier is complete in every frame; what moves is which block is naming its origin.
 *
 * It used to fill an empty form block by block, which is a loading screen — and there is no
 * attractive version of a loading screen. Worse, it emptied itself again on the wrap, so a third of
 * the loop was spent taking apart the record it had just assembled. Showing where each block came
 * from says the thing the section actually claims — that the data is linked, not typed — and it
 * never has to be empty to say it.
 */
const BEAT_HOLDS = [1500, 1100, 1100, 1100, 1100] as const

/** Which block names its origin on each beat. Beat 0 is the plain, complete record. */
/**
 * The order the loop lifts the blocks in: the record's own identity first, then who owns the
 * relationship, then where the client sits, then who to call there. Reading order down the card,
 * which is also the order someone opening a file for the first time takes it in — the old cycle
 * jumped from the address back up to the manager and then down again.
 */
const HIGHLIGHTS = ['none', 'kvk', 'manager', 'address', 'contacts'] as const
type Highlight = (typeof HIGHLIGHTS)[number]

const REST_BEAT = 0 // the complete dossier, nothing singled out

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the block being highlighted. */
function highlightAt(beat: number | null): Highlight {
  return HIGHLIGHTS[beat ?? REST_BEAT] ?? 'none'
}

/** The number beside a block's caption. */
function Count({ total }: { total: number }) {
  return <span className={styles.count}>{total}</span>
}

/**
 * A block's caption: its label on the left and, hard right, the origin pill naming where the data
 * came from. Right-aligned rather than inline — the right-hand column is narrow, and an inline pill
 * runs off the card's edge.
 */
function Caption({ label, count, source, shown }: { label: string; count?: number; source: string; shown: boolean }) {
  return (
    <span
      className="flex items-baseline justify-between gap-1 text-[8.5px] font-bold whitespace-nowrap"
      style={{ color: 'var(--au-text2)', letterSpacing: '0.02em' }}
    >
      <span className="min-w-0">
        {label}
        {count === undefined ? null : (
          <>
            {' '}
            <Count total={count} />
          </>
        )}
      </span>
      <span className={clsx(styles.source, shown && styles.sourceVisible)}>{source}</span>
    </span>
  )
}

/**
 * Dossier visual — the complete client record for Noorderpoort IT B.V.: general info (name, number,
 * contact details, KvK, VAT, IBAN), who owns the relationship, its addresses and its contacts.
 *
 * Every field is present in every frame. The loop lifts one block at a time and names where its
 * data came from — the KvK, the import, the team — which is what "alles gekoppeld" means and what
 * the old fill-the-empty-form version could only assert. `beat === null` (SSR, no-JS, reduced
 * motion) renders the plain complete record with nothing singled out.
 *
 * `compact` is for the homepage's module card, which is a hundred pixels shorter than the panel on
 * the Klantbeheer page: it drops the IBAN and the postal address so the record ends inside the card
 * rather than under its edge.
 */
export function ClientRecordVisual({ className, compact = false }: { className?: string; compact?: boolean }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const highlight = highlightAt(beat)
  // The postal address is the one thing here that a reader loses nothing by not seeing: the
  // business address above it already answers "where does this client sit".
  const addresses = compact ? ADDRESSES.slice(0, 1) : ADDRESSES
  // And one contact rather than two. The point of the block is that a client's people are on the
  // record, which one line makes as well as two — and two lines of name-plus-role in a column this
  // narrow is the densest thing on the card. The count follows the list, so the caption cannot
  // claim more than is shown.
  const contacts = compact ? CONTACTS.slice(0, 1) : CONTACTS

  return (
    <AppUiVisual label={t.screen.clientRecord.caption(SUBJECT.name)} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="client-record" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.word.clients}
            detail={`${SUBJECT.nr} · ${SUBJECT.name}`}
            right={<Tag className="px-1.5 py-[2px] text-[8.5px]">{SUBJECT.form}</Tag>}
          />

          <div className={clsx(styles.columns, 'flex min-h-0 flex-1 gap-3 px-3 py-2.5')}>
            <div className={clsx(styles.leftCol, 'flex flex-1 flex-col gap-2')}>
              <Caption
                label={t.screen.clientRecord.general}
                source={t.screen.clientRecord.sourceKvk}
                shown={highlight === 'kvk'}
              />
              {/* One column, not two: these are all facts about one client, and reading them down
                  a single list is how the record itself reads.
                  The lift wraps the fields and not the caption above them: the caption is already
                  saying where the data came from, through the origin pill it reveals, and lifting it
                  along with the fields made the marked area start at a heading rather than at the
                  thing the heading is about. The relationship manager block was the only one doing
                  it that way round, and it was the one that read correctly. */}
              <div
                className={clsx(
                  'flex flex-1 flex-col justify-between gap-1.5',
                  styles.fieldList,
                  styles.block,
                  highlight === 'kvk' && styles.blockLifted,
                )}
              >
                {fields(t, compact).map((field) => (
                  <div
                    key={field.label}
                    className={clsx('flex min-w-0 flex-col gap-px', styles.field, styles.reveal, styles.revealVisible)}
                  >
                    <span
                      className={clsx(styles.fieldLabel, 'truncate font-semibold whitespace-nowrap')}
                      style={{ color: 'var(--au-text2)' }}
                    >
                      {field.label}
                    </span>
                    <b
                      className={clsx(styles.fieldValue, 'truncate font-semibold tabular-nums')}
                      style={{ color: field.accent ? 'var(--au-accent)' : 'var(--au-text)' }}
                    >
                      {field.value}
                    </b>
                  </div>
                ))}
              </div>
            </div>

            {/* `min-w-0` so the lines in here ellipsize on a narrow card instead of being cut off
                mid-letter by the screen's own clip. */}
            <div
              className={clsx(styles.rightCol, 'flex min-w-0 flex-1 flex-col gap-2 border-l pl-3')}
              style={{ borderColor: 'var(--au-hairline)' }}
            >
              {/* Who at the firm owns this client — the record's first answer, before where it sits
                  and who to call there. Read from the client itself, so it is the same colleague
                  every list on the site puts beside this name. */}
              <Caption
                label={t.screen.clientRecord.accountManager}
                source={t.screen.clientRecord.sourceTeam}
                shown={highlight === 'manager'}
              />
              <div
                className={clsx(
                  'flex items-center gap-2',
                  styles.reveal,
                  styles.revealVisible,
                  styles.block,
                  highlight === 'manager' && styles.blockLifted,
                )}
              >
                <span
                  className="flex size-[22px] shrink-0 items-center justify-center rounded-full text-[8.5px] font-bold text-white"
                  style={{ background: MANAGER.color }}
                >
                  {MANAGER.initials}
                </span>
                <div className="flex min-w-0 flex-col">
                  <span className={clsx(styles.fieldValue, 'truncate font-semibold')}>{MANAGER.name}</span>
                  <span className="truncate text-[8.5px]" style={{ color: 'var(--au-text2)' }}>
                    {t.teamRole[MANAGER.role]}
                  </span>
                </div>
              </div>

              <div className={clsx('flex flex-col gap-2', styles.sideBlock)}>
                <Caption
                  label={t.screen.clientRecord.addresses}
                  source={t.screen.clientRecord.sourceKvk}
                  shown={highlight === 'address'}
                />
                <div
                  className={clsx('flex flex-col gap-2', styles.block, highlight === 'address' && styles.blockLifted)}
                >
                  {addresses.map((address, index) => (
                    <div
                      key={address.street}
                      className={clsx('flex min-w-0 flex-col gap-px', styles.reveal, styles.revealVisible)}
                      style={{ transitionDelay: `${index * 90}ms` }}
                    >
                      <span className={clsx(styles.fieldValue, 'truncate font-semibold')}>
                        {address.street}{' '}
                        <span
                          className="rounded-[3px] px-1 py-px text-[7.5px] font-bold whitespace-nowrap"
                          style={{ color: 'var(--au-text2)', background: 'var(--au-field)' }}
                        >
                          {addressTagLabel(t, address.tag)}
                        </span>
                      </span>
                      <span className="truncate text-[9.5px]" style={{ color: 'var(--au-text2)' }}>
                        {address.place}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={clsx('flex flex-col gap-2', styles.sideBlock)}>
                <Caption
                  label={t.screen.clientRecord.contacts}
                  source={t.screen.clientRecord.sourceImport}
                  shown={highlight === 'contacts'}
                />
                <div
                  className={clsx('flex flex-col gap-2', styles.block, highlight === 'contacts' && styles.blockLifted)}
                >
                  {contacts.map((contact, index) => (
                    <div
                      key={contact.name}
                      className={clsx('flex min-w-0 flex-col items-start gap-1', styles.reveal, styles.revealVisible)}
                      style={{ transitionDelay: `${index * 90}ms` }}
                    >
                      <span className="max-w-full truncate text-[10px] font-semibold whitespace-nowrap">
                        {contact.name}{' '}
                        <span className="font-normal" style={{ color: 'var(--au-text2)' }}>
                          ({contact.nickname})
                        </span>
                      </span>
                      {/* `max-w-full truncate`: the longest of these roles is wider than a phone's
                        column, and without it the chip ran to the card's edge and was cut there. */}
                      <span
                        className="max-w-full truncate rounded-[3px] px-1 py-px text-[7.5px] font-bold whitespace-nowrap"
                        style={{ color: 'var(--au-text2)', background: 'var(--au-field)' }}
                      >
                        {t.contactRole[contact.role]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
